import * as THREE from 'three';
import { clamp } from '../util/format';

export interface SplineFrame {
  distance: number;
  position: THREE.Vector3;
  tangent: THREE.Vector3;
  normal: THREE.Vector3;
  binormal: THREE.Vector3;
  bank: number;
}

export interface SplineSample {
  distance: number;
  position: THREE.Vector3;
  tangent: THREE.Vector3;
  normal: THREE.Vector3;
  binormal: THREE.Vector3;
  quaternion: THREE.Quaternion;
}

function catmull(p0: THREE.Vector3, p1: THREE.Vector3, p2: THREE.Vector3, p3: THREE.Vector3, t: number, target: THREE.Vector3): void {
  const t2 = t * t;
  const t3 = t2 * t;
  target.set(
    0.5 * (2 * p1.x + (-p0.x + p2.x) * t + (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 + (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3),
    0.5 * (2 * p1.y + (-p0.y + p2.y) * t + (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 + (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3),
    0.5 * (2 * p1.z + (-p0.z + p2.z) * t + (2 * p0.z - 5 * p1.z + 4 * p2.z - p3.z) * t2 + (-p0.z + 3 * p1.z - 3 * p2.z + p3.z) * t3),
  );
}

function catmullDerivative(p0: THREE.Vector3, p1: THREE.Vector3, p2: THREE.Vector3, p3: THREE.Vector3, t: number, target: THREE.Vector3): void {
  const t2 = t * t;
  target.set(
    0.5 * ((-p0.x + p2.x) + 2 * (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t + 3 * (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t2),
    0.5 * ((-p0.y + p2.y) + 2 * (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t + 3 * (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t2),
    0.5 * ((-p0.z + p2.z) + 2 * (2 * p0.z - 5 * p1.z + 4 * p2.z - p3.z) * t + 3 * (-p0.z + 3 * p1.z - 3 * p2.z + p3.z) * t2),
  );
}

export function buildSpline(points: THREE.Vector3[], samplesPerSegment = 8, maxBank = 0.14): { frames: SplineFrame[]; length: number } {
  const count = points.length;
  const raw: { position: THREE.Vector3; tangent: THREE.Vector3 }[] = [];
  const position = new THREE.Vector3();
  const tangent = new THREE.Vector3();
  for (let i = 0; i < count; i += 1) {
    const p0 = points[(i - 1 + count) % count];
    const p1 = points[i];
    const p2 = points[(i + 1) % count];
    const p3 = points[(i + 2) % count];
    for (let step = 0; step < samplesPerSegment; step += 1) {
      const t = step / samplesPerSegment;
      catmull(p0, p1, p2, p3, t, position);
      catmullDerivative(p0, p1, p2, p3, t, tangent);
      if (tangent.lengthSq() < 1e-8) tangent.set(0, 0, 1);
      tangent.normalize();
      raw.push({ position: position.clone(), tangent: tangent.clone() });
    }
  }

  const frames: SplineFrame[] = [];
  let distance = 0;
  for (let i = 0; i < raw.length; i += 1) {
    const current = raw[i];
    const next = raw[(i + 1) % raw.length];
    const crossY = current.tangent.x * next.tangent.z - current.tangent.z * next.tangent.x;
    const bank = clamp(-crossY * 28, -maxBank, maxBank);

    const normal = new THREE.Vector3(0, 1, 0);
    normal.addScaledVector(current.tangent, -normal.dot(current.tangent));
    if (normal.lengthSq() < 1e-8) normal.set(0, 1, 0);
    else normal.normalize();
    const binormal = new THREE.Vector3().crossVectors(normal, current.tangent).normalize();
    normal.crossVectors(current.tangent, binormal).normalize();
    if (Math.abs(bank) > 1e-4) {
      normal.applyAxisAngle(current.tangent, bank);
      binormal.applyAxisAngle(current.tangent, bank);
    }

    frames.push({
      distance,
      position: current.position,
      tangent: current.tangent,
      normal,
      binormal,
      bank,
    });
    distance += current.position.distanceTo(next.position);
  }

  return { frames, length: distance };
}

export function sampleSpline(frames: SplineFrame[], distance: number, lapLength: number): SplineSample {
  const length = Math.max(lapLength, 0.001);
  const wrapped = ((distance % length) + length) % length;
  let low = 0;
  let high = frames.length - 1;
  while (low < high) {
    const mid = (low + high + 1) >> 1;
    if (frames[mid].distance <= wrapped) low = mid;
    else high = mid - 1;
  }
  const index = low;
  const a = frames[index];
  const b = frames[(index + 1) % frames.length];
  const span = index === frames.length - 1 ? length - a.distance : Math.max(0.0001, b.distance - a.distance);
  const u = clamp((wrapped - a.distance) / span, 0, 1);
  const tangent = a.tangent.clone().lerp(b.tangent, u).normalize();
  const normal = a.normal.clone().lerp(b.normal, u).normalize();
  const binormal = new THREE.Vector3().crossVectors(normal, tangent).normalize();
  const fixedNormal = new THREE.Vector3().crossVectors(tangent, binormal).normalize();
  const quaternion = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(binormal, fixedNormal, tangent));
  return {
    distance: wrapped,
    position: a.position.clone().lerp(b.position, u),
    tangent,
    normal: fixedNormal,
    binormal,
    quaternion,
  };
}

export interface SplineProjection {
  index: number;
  distance: number;
  /** Centerline height at the closest point. */
  y: number;
  position: THREE.Vector3;
  normal: THREE.Vector3;
  binormal: THREE.Vector3;
}

/** Closest point on the polyline, so a slope is pinned to the road under the cart rather than the last vertex. */
export function projectSpline(
  frames: SplineFrame[],
  position: THREE.Vector3,
  hint: number,
  lapLength: number,
): SplineProjection {
  const count = frames.length;
  const length = Math.max(lapLength, 0.001);
  const hintIndex = ((hint % count) + count) % count;
  const hintDistance = frames[hintIndex]?.distance ?? 0;
  let bestIndex = hintIndex;
  let bestDist = Infinity;
  let bestScore = Infinity;
  let bestU = 0;
  const consider = (index: number, penalize: boolean) => {
    const a = frames[index].position;
    const b = frames[(index + 1) % count].position;
    const abx = b.x - a.x;
    const aby = b.y - a.y;
    const abz = b.z - a.z;
    const apx = position.x - a.x;
    const apy = position.y - a.y;
    const apz = position.z - a.z;
    const ab2 = abx * abx + aby * aby + abz * abz;
    if (ab2 < 1e-8) return;
    const u = clamp((apx * abx + apy * aby + apz * abz) / ab2, 0, 1);
    const dx = apx - abx * u;
    const dy = apy - aby * u;
    const dz = apz - abz * u;
    const dist = dx * dx + dy * dy + dz * dz;
    // Stay on the hint's part of the lap. A folded circuit (and the start/finish
    // seam) can be closer in space than the segment under the cart, and jumping
    // there pins the cart to the wrong chord.
    let extra = 0;
    if (penalize) {
      const span = index === count - 1 ? length - frames[index].distance : Math.max(0.0001, frames[(index + 1) % count].distance - frames[index].distance);
      let candidate = frames[index].distance + span * u;
      if (candidate >= length) candidate -= length;
      let delta = candidate - hintDistance;
      delta = ((delta % length) + length) % length;
      if (delta > length / 2) delta -= length;
      const along = Math.abs(delta);
      if (along > 28) extra = (along - 28) * 8;
    }
    const score = dist + extra;
    if (score < bestScore) {
      bestScore = score;
      bestDist = dist;
      bestIndex = index;
      bestU = u;
    }
  };
  for (let delta = -24; delta <= 24; delta += 1) consider((bestIndex + delta + count * 4) % count, true);
  if (bestDist > 36 * 36) {
    bestDist = Infinity;
    bestScore = Infinity;
    for (let index = 0; index < count; index += 1) consider(index, false);
  }
  const frame = frames[bestIndex];
  const next = frames[(bestIndex + 1) % count];
  const span = bestIndex === count - 1 ? length - frame.distance : Math.max(0.0001, next.distance - frame.distance);
  let distance = frame.distance + span * bestU;
  if (distance >= length) distance -= length;
  const point = frame.position.clone().lerp(next.position, bestU);
  const tangent = frame.tangent.clone().lerp(next.tangent, bestU);
  if (tangent.lengthSq() < 1e-8) tangent.copy(frame.tangent);
  tangent.normalize();
  const normal = frame.normal.clone().lerp(next.normal, bestU);
  if (normal.lengthSq() < 1e-8) normal.set(0, 1, 0);
  normal.normalize();
  const binormal = new THREE.Vector3().crossVectors(normal, tangent);
  if (binormal.lengthSq() < 1e-8) binormal.copy(frame.binormal);
  binormal.normalize();
  const fixedNormal = new THREE.Vector3().crossVectors(tangent, binormal);
  if (fixedNormal.lengthSq() < 1e-8) fixedNormal.copy(normal);
  fixedNormal.normalize();
  return { index: bestIndex, distance, y: point.y, position: point, normal: fixedNormal, binormal };
}

export function nearestFrame(frames: SplineFrame[], position: THREE.Vector3, hint = 0): { index: number; distance: number } {
  const count = frames.length;
  let bestIndex = ((hint % count) + count) % count;
  let best = Infinity;
  const search = (window: number) => {
    for (let delta = -window; delta <= window; delta += 1) {
      const index = (bestIndex + delta + count * 4) % count;
      const d = frames[index].position.distanceToSquared(position);
      if (d < best) {
        best = d;
        bestIndex = index;
      }
    }
  };
  search(18);
  if (best > 36 * 36) {
    best = Infinity;
    for (let index = 0; index < count; index += 1) {
      const d = frames[index].position.distanceToSquared(position);
      if (d < best) {
        best = d;
        bestIndex = index;
      }
    }
  }
  return { index: bestIndex, distance: frames[bestIndex].distance };
}
